// Parts every Chart screen shares (Chart UX pass 2026-10-21): the edge-to-edge frame under the
// 56 px location header, the body gutters, the Chart crumb, and the seat identity Rippling,
// ChartHop and Pingboard put on a card: the holder's avatar and name first, the seat's title
// under it, an open seat drawn as such. Composes @companyos/ui only.
import {
  AvatarStack,
  AvatarTile,
  type AvatarTileSize,
  cn,
  type LocationCrumb,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { Link } from "@tanstack/react-router";
import { ArmchairIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { keepFocus } from "../../focus/focus";
import {
  DeAvatar,
  DeHoverCard,
  ringHint,
  usePresenceCardScope,
  usePresenceOf,
} from "../chat/de-presence";
import type { CompanyAccess } from "../company";
import type { ChartSeat } from "./chart-model";

/** The screen: a column that fills the main region (the header, then a body). */
export function ChartFrame({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("flex min-h-full min-w-0 flex-col", className)} {...props} />;
}

/**
 * The org chart route's root: the mode root (ModeGate) becomes a full-height column, so the
 * canvas fits the tree to the main pane and scrolls itself, like the chat view.
 */
export function ChartModeRoot({ children }: { children: ReactNode }) {
  return (
    <div className="h-full [&>[data-mode-root]]:flex [&>[data-mode-root]]:h-full [&>[data-mode-root]]:flex-col">
      {children}
    </div>
  );
}

/** The body under the header and toolbar: 24 px gutters (16 on phones). */
export function ChartBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="chart-body"
      className={cn("flex min-w-0 flex-1 flex-col gap-4 px-6 py-4 max-md:px-4", className)}
      {...props}
    />
  );
}

/** "Chart" as the first crumb of every Chart screen, back to the org chart. */
export const chartCrumb = (company: CompanyAccess): LocationCrumb => ({
  label: "Chart",
  render: <Link to="/c/$slug/kpis/chart" params={{ slug: company.slug }} search={keepFocus} />,
});

/** "Setup" as the first crumb of Setup › Teams / Seats / Finance. */
export const setupCrumb = (company: CompanyAccess): LocationCrumb => ({
  label: "Setup",
  render: <Link to="/c/$slug/setup" params={{ slug: company.slug }} search={keepFocus} />,
});

type Holder = ChartSeat["holders"][number];

export const holderKind = (h: Pick<Holder, "membership_kind">) =>
  h.membership_kind === "digital_employee" ? ("agent" as const) : ("person" as const);

/** "Dana Whitfield" / "Dana Whitfield, Riley" / "" for an open seat. */
export const holderNames = (seat: Pick<ChartSeat, "holders">) =>
  seat.holders.map((h) => workerName(h.display_name)).join(", ");

/** The holders' names as inline text; a digital employee's name carries the DE badge. */
export function HolderNamesBadged({ seat }: { seat: Pick<ChartSeat, "holders"> }) {
  return (
    <>
      {seat.holders.map((h, i) => (
        <span key={h.membership_id} className="inline-flex items-center gap-1">
          {h.membership_kind === "digital_employee" ? <WorkerBadge kind="de" size="sm" /> : null}
          {workerName(h.display_name)}
          {i < seat.holders.length - 1 ? "," : ""}
        </span>
      ))}
    </>
  );
}

/** The seat's holders as avatar items (a DE is the bot tile). */
export const holderItems = (seat: Pick<ChartSeat, "holders">) =>
  seat.holders.map((h) => ({ name: workerName(h.display_name), kind: holderKind(h) }));

/**
 * The card's avatar: the one holder's tile, a stack for several, the armchair for an open seat.
 * `size` is the tile size; a stack is one step smaller so it keeps the card's height.
 */
export function SeatAvatar({
  seat,
  size = "lg",
  className,
}: {
  seat: Pick<ChartSeat, "holders" | "title">;
  size?: AvatarTileSize;
  className?: string;
}) {
  // CHAT-PRESENCE: a DE holder wears its ring (the screen provides the rings; none elsewhere).
  const only = seat.holders.length === 1 ? seat.holders[0] : undefined;
  const ring = usePresenceOf(
    only?.membership_kind === "digital_employee" ? only.membership_id : undefined,
  );
  const card = usePresenceCardScope();
  if (seat.holders.length === 0)
    return (
      <span
        role="img"
        aria-label={`${seat.title}: open seat`}
        data-slot="open-seat-avatar"
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-sm border border-dashed border-strong text-faint",
          size === "lg"
            ? "size-8 [&_svg]:size-4"
            : size === "xs"
              ? "size-4 rounded-xs [&_svg]:size-2.5"
              : "size-6 [&_svg]:size-3.5",
          className,
        )}
      >
        <ArmchairIcon aria-hidden="true" />
      </span>
    );
  const [first] = seat.holders;
  if (seat.holders.length === 1 && first && ring) {
    const avatar = (
      <DeAvatar
        name={first.display_name}
        state={ring.state}
        hint={ringHint(ring)}
        size={size}
        className={cn("shrink-0", className)}
      />
    );
    // CHAT-PRESENCE: hovering the ring opens the DE's card, as in the Team list.
    return card ? (
      <DeHoverCard
        {...card}
        de={{ id: first.membership_id, name: first.display_name }}
        presence={ring}
      >
        {avatar}
      </DeHoverCard>
    ) : (
      avatar
    );
  }
  if (seat.holders.length === 1 && first)
    return (
      <AvatarTile
        name={workerName(first.display_name)}
        kind={holderKind(first)}
        size={size}
        className={cn("shrink-0", className)}
      />
    );
  return (
    <AvatarStack
      items={holderItems(seat)}
      size={size === "lg" ? "md" : size === "xs" ? "xs" : "sm"}
      max={3}
      className={className}
    />
  );
}

/**
 * Name over title (the person, then the seat): "Jordan Lee" / "Recruiting lead". An open seat
 * reads "Open seat" muted over its title; `after` sits on the title line (a role, a count).
 */
export function SeatIdentity({
  seat,
  name,
  after,
  className,
}: {
  seat: Pick<ChartSeat, "holders" | "title">;
  /** The name line's element (a link to the seat page, usually). */
  name: ReactNode;
  after?: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn("flex min-w-0 flex-col", className)}>
      <span className="flex min-w-0 items-center gap-1.5 text-sm leading-5 font-medium text-foreground">
        {name}
      </span>
      <span className="flex min-w-0 items-center gap-1.5 text-xs leading-4 text-muted-foreground">
        <span className="truncate">{seat.title}</span>
        {after}
      </span>
    </span>
  );
}

/** What the name line says: the holders, or "Open seat" for nobody. */
export const seatNameText = (seat: Pick<ChartSeat, "holders" | "title">) =>
  seat.holders.length ? holderNames(seat) : "Open seat";
